import React from 'react';
import styled from 'styled-components';
import { BasicFormState } from 'hooks/useForm';
import filterableUserFields from './filterableUserFields';
import { tieredCapabilities, UserFormField } from './useUserForm';

const Table = styled.table`
  border-collapse: collapse;
  margin: 6px 0 10px;

  th {
    font-size: 0.8em;
    font-weight: 600;
    color: rgb(110, 110, 110);
    padding: 2px 10px;
    text-align: center;
    white-space: nowrap;
  }

  th:first-child {
    text-align: left;
    padding-left: 0;
  }

  td {
    padding: 3px 10px;
    border-top: 1px solid #b5b3b35c;
    text-align: center;
  }

  td:first-child {
    text-align: left;
    padding-left: 0;
  }
`;

const Capability = styled.div`
  display: grid;
  grid-template-columns: 22px auto;
  align-items: center;
`;

type Level = 'yes' | 'emergency' | 'no';

const LEVELS: { level: Level; label: string; title: string }[] = [
  { level: 'yes', label: 'Ja', title: 'Behörig' },
  {
    level: 'emergency',
    label: 'I nödfall',
    title:
      'Behörig, men ruttplaneringen väljer helst någon annan och flaggar paketet',
  },
  { level: 'no', label: 'Nej', title: 'Inte behörig' },
];

interface Props {
  state: BasicFormState<UserFormField>;
  disabled: boolean;
  setMultiple(values: Partial<BasicFormState<UserFormField>>): void;
}

/**
 * Behörigheter med tre nivåer (2026-10-11): Ja, I nödfall, Nej. "I nödfall" är fortfarande
 * behörig — uppdrag kan tilldelas — men ruttplaneringen lägger ett påslag och flaggar paketet,
 * så att trafikledaren ser att någon som helst inte ska köra det har föreslagits.
 *
 * Varje rad skriver två fält: Can… och …EmergencyOnly. Därför radioknappar och setMultiple,
 * inte två fristående kryssrutor som kan hamna i "Nej + nödfall".
 */
const CapabilityLevelTable: React.FC<Props> = ({
  state,
  disabled,
  setMultiple,
}) => {
  const levelOf = (can: UserFormField, emergencyOnly: UserFormField): Level =>
    state[can] !== 'true'
      ? 'no'
      : state[emergencyOnly] === 'true'
        ? 'emergency'
        : 'yes';

  return (
    <Table>
      <thead>
        <tr>
          <th>Behörighet</th>
          {LEVELS.map(({ level, label, title }) => (
            <th key={level} title={title}>
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {tieredCapabilities.map(({ can, emergencyOnly }) => {
          const field = can as keyof typeof filterableUserFields;
          const current = levelOf(can, emergencyOnly);
          return (
            <tr
              aria-label={filterableUserFields[field].title}
              key={can}
              role="radiogroup"
            >
              <td>
                <Capability>
                  {filterableUserFields[field].icon(true)}
                  {filterableUserFields[field].title}
                </Capability>
              </td>
              {LEVELS.map(({ level, label }) => (
                <td key={level}>
                  <input
                    aria-label={`${filterableUserFields[field].title}: ${label}`}
                    checked={current === level}
                    disabled={disabled}
                    name={`capability-${can}`}
                    onChange={() =>
                      setMultiple({
                        [can]: level === 'no' ? 'false' : 'true',
                        [emergencyOnly]:
                          level === 'emergency' ? 'true' : 'false',
                      })
                    }
                    type="radio"
                  />
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default CapabilityLevelTable;
